import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { Dashboard } from "../pages/dashboard/Dashboard";
import Role from "../pages/user-management/role";
import NotFound from "pages/error-page/NotFound";
import { authRoutesArray } from "constant/route/authRoutes";
import { privateRoutes } from "constant/route/privateRoutes";
import PageLoader from "components/base/PageLoader";
import SubscriptionUsage from "pages/pricing/subscription-usage/SubscriptionUsage";
import ViewRole from "pages/user-management/role/ViewRole";
import { featureObject } from "helpers/privileges/utils";
import { useFeatureAccess } from "hooks‬/custome-hooks/useFeatureAccess";
import { usePrivilege } from "hooks‬/PrivilegeContext";
import UserInvitationForm from "pages/user-management/users/UserInvitationForm";
const CompanyProfile = lazy(
  () => import("../pages/company-managment/company-profile")
);
const CompanyBranches = lazy(
  () => import("../pages/company-managment/company-branches")
);
const Permissions = lazy(() => import("../pages/user-management/permissions"));
const Users = lazy(() => import("../pages/user-management/users"));
const InvoicePaymentPage = lazy(
  () => import("pages/pricing/InvoicePaymentPage")
);
const MyProfile = lazy(() => import("../pages/my-profile"));
const CompanyPreferences = lazy(
  () => import("../pages/company-managment/company-preferences")
);
const CompanyRebranding = lazy(
  () => import("../pages/company-managment/company-rebranding")
);

const PlanHandlerPage = lazy(() => import("../pages/pricing/plans"));
const IntegrationsPage = lazy(() => import("../pages/integrations/IntegrationsPage"));

interface PermissionRouteProps {
  feature: string;
  children: React.ReactNode;
}
const PermissionRoute: React.FC<PermissionRouteProps> = ({
  feature,
  children,
}) => {
  const { canRead } = useFeatureAccess(feature);
  const { privileges } = usePrivilege();
  if (Object.keys(privileges).length === 0) {
    return <PageLoader />;
  } else if (!canRead) {
    return <NotFound />;
  }

  return <>{children}</>;
};

export default PermissionRoute;

const PrivateRoutes = () => {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path={privateRoutes.dashboard} element={<Dashboard />} />

        {/* user-management */}
        <Route
          path={privateRoutes.userManagementUsers}
          element={
            <PermissionRoute feature={featureObject.USERS}>
              <Users />
            </PermissionRoute>
          }
        />
        <Route
          path={privateRoutes.userManagementUsers + "/user-invitation"}
          element={
            <PermissionRoute feature={featureObject.USERS}>
              <UserInvitationForm />
            </PermissionRoute>
          }
        />
        <Route
          path={privateRoutes.userManagementRoles}
          element={
            <PermissionRoute feature={featureObject.USR_ROLE}>
              <Role />
            </PermissionRoute>
          }
        />
        <Route
          path={privateRoutes.userManagementRoles + "/:roleGuid"}
          element={
            <PermissionRoute feature={featureObject.USR_ROLE}>
              <ViewRole />
            </PermissionRoute>
          }
        />
        <Route
          path={privateRoutes.userManagementPermissions}
          element={<Permissions />}
        />

        {/* company-management */}
        <Route
          path={privateRoutes.companyManagementProfile}
          element={
            <PermissionRoute feature={featureObject.COMPANY}>
              <CompanyProfile />
            </PermissionRoute>
          }
        />
        <Route
          path={privateRoutes.companyManagementCompanyBranches}
          element={
            <PermissionRoute feature={featureObject.BRANCH}>
              <CompanyBranches />
            </PermissionRoute>
          }
        />
        <Route
          path={privateRoutes.companyManagementRebranding}
          element={
            <PermissionRoute feature={featureObject.REBRAND}>
              <CompanyRebranding />
            </PermissionRoute>
          }
        />
        <Route
          path={privateRoutes.companyManagementPreferences}
          element={
            <PermissionRoute feature={featureObject.COM_PREF}>
              <CompanyPreferences />
            </PermissionRoute>
          }
        />
        <Route
          path={privateRoutes.companyManagementIntegrations}
          element={
            <PermissionRoute feature={featureObject.COMPANY}>
              <IntegrationsPage />
            </PermissionRoute>
          }
        />

        {/* pricing */}
        <Route
          path={privateRoutes.Pricing}
          element={
            <PermissionRoute feature={featureObject.SUBSCRIPTION}>
              <PlanHandlerPage />
            </PermissionRoute>
          }
        />

        <Route
          path={privateRoutes.subscriptionUsage}
          element={
            <PermissionRoute feature={featureObject.SUBS_USG}>
              <SubscriptionUsage />
            </PermissionRoute>
          }
        />
        <Route
          path={privateRoutes.paymentProcess + "/:pricingPlanGuid"}
          element={
            <PermissionRoute feature={featureObject.SUBSCRIPTION}>
              <InvoicePaymentPage />
            </PermissionRoute>
          }
        />

        {/* <Route
          path={privateRoutes.productSubscription}
          element={<ProductSubscription />}
        />
        <Route
          path={privateRoutes.billingPayment}
          element={<BillingPayemnt />}
        /> */}

        <Route
          path="auth/*"
          element={<Navigate to={privateRoutes.companyManagementProfile} />}
        />

        {/* <Route
          path={privateRoutes.companyManagementServices}
          element={<CompanyServices />}
        /> */}

        {/* <Route
          path={privateRoutes.companyManagementLegal}
          element={<CompanyLegal />}
        /> */}

        <Route path={privateRoutes.myProfile} element={<MyProfile />} />

        {authRoutesArray.map((path) => (
          <Route
            path={path}
            element={<Navigate to={privateRoutes.dashboard} />}
            key={path}
          />
        ))}

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

export { PrivateRoutes };
